import React, { useEffect, useRef, useState } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useSelector } from '@xstate/react';
import { Loader2 } from 'lucide-react';
import {
  BrowserRouter,
  Navigate,
  Routes as ReactRoutes,
  Route,
  useLocation,
  useNavigate,
} from 'react-router-dom';

import { useUser } from '@/hooks/auth';
import { useStaleTabResync } from '@/hooks/use-stale-tab-resync';
import { signOut } from '@/lib/auth';
import { ActivateAccountPage } from '@/modules/authorization/account-activation/pages';
import { ResetPasswordPage } from '@/modules/authorization/reset-password/pages';
import { SignInPage } from '@/modules/authorization/sign-in/pages';
import { ClientDetailsPage, ClientsPage } from '@/modules/clients';
import {
  ProjectsReconciliationStepPage,
  TeamReconciliationStepPage,
} from '@/modules/common/components/templates/data-reconciliation';
import { ForbiddenPage } from '@/modules/common/pages/forbidden-page';
import { SubscriptionExpiredPage } from '@/modules/common/pages/subscription-expired-page';
import { ClientsReconciliationStepPage } from '@/modules/data-reconciliation/pages/clients-reconciliation-step-page';
import { DataReconciliationPage } from '@/modules/data-reconciliation/pages/data-reconciliation-page';
import { FramerMockupsPage } from '@/modules/debug/pages/framer-mockups-page';
import { NotificationsDebugPage } from '@/modules/debug/pages/notifications-debug-page';
import { BillDetailsPage, BillsPage, GenerateBillsPage } from '@/modules/finance/bills';
import { GenerateInvoicesPage, InvoiceDetailsPage, InvoicesPage } from '@/modules/finance/invoices';
import { OverheadsPage } from '@/modules/finance/overheads';
import { ClientsOnboardingStepPage } from '@/modules/onboarding/clients-setup/pages/clients-onboarding-step-page';
import OnboardingError from '@/modules/onboarding/common/pages/onboarding-error';
import { OnboardingCompletionPage } from '@/modules/onboarding/completion';
import { IntegrationsOnboardingStepPage } from '@/modules/onboarding/integrations-setup';
import { OrganizationOnboardingStepPage } from '@/modules/onboarding/organization-setup';
import { ProjectsOnboardingStepPage } from '@/modules/onboarding/projects-setup/pages/projects-onboarding-step-page';
import { TeamOnboardingStepPage } from '@/modules/onboarding/team-setup/pages/team-onboarding-step-page';
import { PlannerPage } from '@/modules/planner';
import { ProjectDetailsPage, ProjectsPage } from '@/modules/projects';
import { ReportBuilderPage, ReportsPage } from '@/modules/reports';
import {
  APIPage,
  DepartmentsPage,
  FinancePage,
  IntegrationsPage,
  MembersCustomAttributesPage,
  MembersPage,
  OAuthCallbackPage,
  OrgSettingsPage,
  PasswordPage,
  PermissionsPage,
  ProfileInfoPage,
  RolesPage,
  SenioritiesPage,
  SettingsPage,
  TagsPage,
  TimeOffSettingsPage,
  TimeOffTypeDetailsPage,
} from '@/modules/settings';
import { TeamMemberDetailsPage, TeamPage } from '@/modules/team';
import { TimeOffApprovalsPage } from '@/modules/time-off/pages/time-off-approvals-page';
import { TimeOffRequestDetailsPage } from '@/modules/time-off/pages/time-off-request-details-page';
import { TimeOffsPage } from '@/modules/time-off/pages/time-offs-page';
import { TimeTrackingApprovalsPage, TimeTrackingPage } from '@/modules/time-tracking';
import OnboardingPrivatePage from '@/router/page/onboarding-private-page';
import PrivatePage from '@/router/page/private-page';
import PublicPage from '@/router/page/public-page';
import {
  AuthenticatedRoutes,
  CommonRoutes,
  OnboardingRoutes,
  SettingsRoutes,
  UnauthenticatedRoutes,
} from '@/router/routes';
import { InitializedRootEvent } from '@/router/state/root/component/root-event';
import { RootStates } from '@/router/state/root/component/root-states';
import { useRootActorRef, useRootSelector } from '@/router/state/root/util/context/helpers';
import { RootContextProvider } from '@/router/state/root/util/context/root-context';
import { getRouteFromState } from '@/router/state/root/util/get-route-from-state';
import { setRootActorRef } from '@/utils/auth-actor';
import { logger } from '@/utils/logger/logger';
import { setNavigate } from '@/utils/navigation';

export const AppRouter: React.FC = () => {
  return (
    <BrowserRouter>
      <RootContextProvider>
        <AppRoutes />
      </RootContextProvider>
    </BrowserRouter>
  );
};

const TAG = 'AppRouter:';

const AppRoutes: React.FC = () => {
  const { user, isLoading } = useUser();
  const location = useLocation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // One authoritative re-read of the cache whenever the user returns to a tab
  // that has been idle for a long time, so no screen ever acts on a snapshot
  // from hours ago.
  useStaleTabResync();

  useEffect(() => {
    setNavigate(navigate);
  }, [navigate]);

  const actorRef = useRootActorRef();

  useEffect(() => {
    setRootActorRef(actorRef);
  }, [actorRef]);
  const state = useRootSelector(state => state);
  const isColdStart = useRootSelector(state => state.context.isColdStart);

  const onboardingMachine = state.children.onboardingMachine;
  const onboardingState = useSelector(onboardingMachine, state => state);

  // Track user changes and clear cache when user changes
  const prevUserRef = useRef<any>(null);

  useEffect(() => {
    const prevUser = prevUserRef.current;
    const currentUserId = user ? JSON.stringify(user) : null;
    const prevUserId = prevUser ? JSON.stringify(prevUser) : null;

    // If user changed (different user logged in or logged out)
    if (prevUserId !== currentUserId) {
      if (prevUserId) {
        // User changed or logged out - clear all cached data
        queryClient.cancelQueries();
        queryClient.clear();
      }
      prevUserRef.current = user;
    }
  }, [user, queryClient]);

  // Initialize root state machine with user and intended route.
  // If the user is authenticated but navigating to /activate-account, sign them
  // out first and treat them as unauthenticated — otherwise the machine transitions
  // to Authenticated and redirects them away from the activation page (infinite loading).
  useEffect(() => {
    if (!isLoading) {
      const isActivateAccountRoute = location.pathname === UnauthenticatedRoutes.ActivateAccount;
      if (user && isActivateAccountRoute) {
        signOut(queryClient).finally(() => {
          actorRef.send(new InitializedRootEvent({ user: null, intendedRoute: location.pathname }));
        });
      } else {
        actorRef.send(new InitializedRootEvent({ user, intendedRoute: location.pathname }));
      }
    }
  }, [isLoading]);

  // Sync onboarding machine with browser back/forward navigation
  // When user navigates via browser buttons, this detects the location change
  // and updates the machine state to match
  const prevLocationPathRef = React.useRef<string>(location.pathname);

  useEffect(() => {
    if (!state.matches({ [RootStates.Authenticated]: 'onboarding' }) || !onboardingMachine) {
      return;
    }

    const currentRoute = location.pathname as OnboardingRoutes;
    const prevRoute = prevLocationPathRef.current;
    const machineRoute = onboardingMachine.getSnapshot().value as string;

    prevLocationPathRef.current = currentRoute;

    // Detect browser navigation: location changed but machine state didn't
    const isBrowserNavigation =
      currentRoute !== prevRoute &&
      currentRoute !== machineRoute &&
      Object.values(OnboardingRoutes).includes(currentRoute);

    if (isBrowserNavigation) {
      onboardingMachine.send({
        type: 'FETCH_NEXT_STEP_SUCCESS',
        targetRoute: currentRoute,
      });
    }
  }, [location.pathname, state, onboardingMachine]);

  // Navigate when machine state changes
  // This watches for machine state changes and triggers navigation when needed
  // Only applies during initialization and onboarding - authenticated workspace uses React Router directly
  const [targetRoute, setTargetRoute] = useState<string | null>(null);

  useEffect(() => {
    const route = getRouteFromState(state, location.pathname);

    // If no route from state machine (e.g., in main workspace), clear target route
    if (route === undefined) {
      if (targetRoute !== null) {
        setTargetRoute(null);
      }
      return;
    }

    // Clear target route if already at destination
    if (route === location.pathname) {
      if (targetRoute !== null) {
        setTargetRoute(null);
      }
      return;
    }

    // Set new target route if different from current
    if (route !== targetRoute) {
      setTargetRoute(route);
    }
  }, [state, onboardingState, location.pathname, targetRoute]);

  useEffect(() => {
    logger.trace(TAG, 'Target route changed:', targetRoute);
    if (targetRoute && targetRoute !== location.pathname) {
      navigate(targetRoute);
    }
  }, [targetRoute, navigate, location.pathname]);

  if (location.pathname == '/' || (state.context.isLoading && isColdStart)) {
    return (
      <div className="flex h-screen w-screen items-center justify-center">
        <Loader2 className="animate-spin" />
      </div>
    );
  }

  return (
    <ReactRoutes>
      {/* PRIVATE PAGES */}
      <Route element={<PrivatePage />}>
        <Route path={AuthenticatedRoutes.Dashboard} element={<div>Dashboard</div>} />
        <Route path={AuthenticatedRoutes.Planner} element={<PlannerPage />} />
        <Route path={AuthenticatedRoutes.TimeTracking} element={<TimeTrackingPage />} />
        <Route
          path={AuthenticatedRoutes.TimeTrackingApprovals}
          element={<TimeTrackingApprovalsPage />}
        />
        <Route path={AuthenticatedRoutes.TimeOff} element={<TimeOffsPage />} />
        {/* Static + nested paths registered before the `:requestId` route so they
            win the match (create, approvals list, approval details). */}
        <Route path={AuthenticatedRoutes.TimeOffApprovals} element={<TimeOffApprovalsPage />} />
        <Route
          path={AuthenticatedRoutes.TimeOffRequestCreate}
          element={<TimeOffRequestDetailsPage />}
        />
        <Route
          path={AuthenticatedRoutes.TimeOffApprovalDetails}
          element={<TimeOffRequestDetailsPage />}
        />
        <Route
          path={AuthenticatedRoutes.TimeOffRequestDetails}
          element={<TimeOffRequestDetailsPage />}
        />
        <Route path={AuthenticatedRoutes.Team} element={<TeamPage />} />
        <Route
          path={AuthenticatedRoutes.TeamMemberChangeEdit}
          element={<TeamMemberDetailsPage />}
        />
        <Route
          path={AuthenticatedRoutes.TeamMemberTimeOffRequestDetails}
          element={<TimeOffRequestDetailsPage />}
        />
        <Route path={AuthenticatedRoutes.TeamMemberDetails} element={<TeamMemberDetailsPage />} />
        <Route path={SettingsRoutes.TimeOffDetails} element={<TimeOffTypeDetailsPage />} />
        <Route path={AuthenticatedRoutes.Projects} element={<ProjectsPage />} />
        <Route path={AuthenticatedRoutes.ProjectChangeEdit} element={<ProjectDetailsPage />} />
        <Route path={AuthenticatedRoutes.ProjectDetails} element={<ProjectDetailsPage />} />
        <Route path={AuthenticatedRoutes.Clients} element={<ClientsPage />} />
        <Route path={AuthenticatedRoutes.ClientDetails} element={<ClientDetailsPage />} />
        <Route path={AuthenticatedRoutes.Invoices} element={<InvoicesPage />} />
        <Route path={AuthenticatedRoutes.GenerateInvoices} element={<GenerateInvoicesPage />} />
        <Route path={AuthenticatedRoutes.InvoiceDetails} element={<InvoiceDetailsPage />} />
        <Route path={AuthenticatedRoutes.Bills} element={<BillsPage />} />
        <Route path={AuthenticatedRoutes.BillDetails} element={<BillDetailsPage />} />
        <Route path={AuthenticatedRoutes.GenerateBills} element={<GenerateBillsPage />} />
        <Route path={AuthenticatedRoutes.Overheads} element={<OverheadsPage />} />
        <Route path={AuthenticatedRoutes.Settings} element={<SettingsPage />}>
          <Route index element={<Navigate to={SettingsRoutes.ProfileInfo} replace />} />
          <Route path="profile-info" element={<ProfileInfoPage />} />
          <Route path="password" element={<PasswordPage />} />
          <Route path="departments" element={<DepartmentsPage />} />
          <Route path="seniorities" element={<SenioritiesPage />} />
          <Route path="roles" element={<RolesPage />} />
          <Route path="general" element={<OrgSettingsPage />} />
          <Route path="finance" element={<FinancePage />} />
          <Route path="time-off" element={<TimeOffSettingsPage />} />
          <Route path="integrations" element={<IntegrationsPage />} />
          <Route path="api" element={<APIPage />} />
          <Route path="custom-attributes" element={<MembersCustomAttributesPage />} />
          <Route path="tags" element={<TagsPage />} />
          <Route path="members" element={<MembersPage />} />
          <Route path="permissions" element={<PermissionsPage />} />
          {import.meta.env.DEV && (
            <Route path="debug-notifications" element={<NotificationsDebugPage />} />
          )}
          {import.meta.env.DEV && <Route path="framer-mockups" element={<FramerMockupsPage />} />}
        </Route>
        <Route path={AuthenticatedRoutes.DataReconciliation} element={<DataReconciliationPage />}>
          <Route path="team" element={<TeamReconciliationStepPage />} />
          <Route path="clients" element={<ClientsReconciliationStepPage />} />
          <Route path="projects" element={<ProjectsReconciliationStepPage />} />
        </Route>
        <Route path={AuthenticatedRoutes.Reports} element={<ReportsPage />} />
        <Route path={AuthenticatedRoutes.ReportDetails} element={<ReportBuilderPage />} />
        <Route path={AuthenticatedRoutes.Forbidden} element={<ForbiddenPage />} />
      </Route>

      {/* ONBOARDING PAGES */}
      <Route element={<OnboardingPrivatePage />}>
        <Route
          path={OnboardingRoutes.OnboardingOrganization}
          element={<OrganizationOnboardingStepPage />}
        />
        <Route
          path={OnboardingRoutes.OnboardingIntegrations}
          element={<IntegrationsOnboardingStepPage />}
        />
        <Route path={OnboardingRoutes.OnboardingTeam} element={<TeamOnboardingStepPage />} />
        <Route path={OnboardingRoutes.OnboardingClients} element={<ClientsOnboardingStepPage />} />
        <Route
          path={OnboardingRoutes.OnboardingProjects}
          element={<ProjectsOnboardingStepPage />}
        />
        <Route path={OnboardingRoutes.OnboardingError} element={<OnboardingError />} />
      </Route>

      {/* ONBOARDING COMPLETION */}
      <Route path={OnboardingRoutes.OnboardingCompletion} element={<OnboardingCompletionPage />} />

      {/* PUBLIC PAGES */}
      <Route element={<PublicPage />}>
        <Route path={UnauthenticatedRoutes.Register} element={<div>Register</div>} />
        <Route path={UnauthenticatedRoutes.Login} element={<SignInPage />} />
        <Route path={UnauthenticatedRoutes.ActivateAccount} element={<ActivateAccountPage />} />
        <Route path={UnauthenticatedRoutes.ResetPassword} element={<ResetPasswordPage />} />
      </Route>
      {/*
        OAuth callback is a transient redirect page that processes the provider's
        response and immediately navigates onward. It must NOT be wrapped in
        PrivatePage/AuthenticatedTemplate — doing so briefly mounts the
        notification-permission logic mid-onboarding, flashing the modal (OPS-1427).
        It renders standalone (just a spinner) regardless of auth/onboarding state.
      */}
      <Route
        path={AuthenticatedRoutes.OnboardingIntegrationsOAuthCallback}
        element={<OAuthCallbackPage />}
      />
      <Route path={AuthenticatedRoutes.BillingExpired} element={<SubscriptionExpiredPage />} />
      <Route path={CommonRoutes.TermsOfService} element={<div>Terms</div>} />
      <Route path={CommonRoutes.PrivacyPolicy} element={<div>Privacy</div>} />
      <Route path="*" element={<div>Not Found</div>} />
    </ReactRoutes>
  );
};
